import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.email || !formData.mensaje) {
      alert('Por favor, completa todos los campos obligatorios.');
      return;
    }

    if (formData.mensaje.length > 500) {
      alert('El mensaje no puede superar los 500 caracteres.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4hjui6usuhg53kqah90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          asunto: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: '¿Cuánto dura el programa completo?',
      answer: 'El programa completo tiene una duración de 6 meses desde tu inscripción. Tendrás 360 horas distribuidas en 4 módulos principales. Puedes estudiar a tu propio ritmo, con acceso ilimitado durante todo el periodo de formación. Al finalizar los 6 meses, la aplicación se bloqueará automáticamente, quedando solo la información esencial disponible.'
    },
    {
      question: '¿La certificación es oficial?',
      answer: 'Al finalizar, recibirás un certificado de aprovechamiento de AULA K9 que acredita que has completado la formación ajustada al Real Decreto 545/2023. Este NO es un Certificado de Profesionalidad oficial. Si deseas obtener el Certificado de Profesionalidad oficial del SEPE, te ayudamos y asesoramos en todo el proceso de solicitud.'
    },
    {
      question: '¿Necesito experiencia previa?',
      answer: 'No es necesaria experiencia previa. Nuestro programa está diseñado para formar desde cero a profesionales del adiestramiento canino, aunque también es ideal para quienes ya trabajan en el sector y desean certificarse oficialmente.'
    },
    {
      question: '¿Cómo funciona la modalidad online?',
      answer: 'Tendrás acceso 24/7 a nuestra plataforma con vídeos, material didáctico descargable, cuestionarios interactivos y soporte de tutores expertos. Las tutorías de dudas se realizan los sábados de 10h a 12h. Puedes enviar tus dudas de domingo a jueves.'
    },
    {
      question: '¿Cómo funcionan los exámenes?',
      answer: 'Cada módulo tiene un cuestionario de 25 preguntas con 3 opciones de respuesta. Necesitas al menos 20 respuestas correctas para aprobar. Tienes 2 intentos por examen. Al segundo intento, el examen se bloqueará.'
    },
    {
      question: '¿Cómo accedo al curso?',
      answer: 'Al matricularte, recibirás automáticamente un número de matrícula como expediente asociado a tu nombre. Este número junto con tu PIN de acceso te permitirán entrar al campus virtual durante los 6 meses de formación.'
    },
    {
      question: '¿Qué pasa después de los 6 meses?',
      answer: 'Al finalizar los 6 meses, la aplicación se bloqueará automáticamente. Solo quedará disponible la información esencial. Las tutorías y materiales adicionales dejarán de estar accesibles. Si deseas continuar o repasar unidades específicas, podrás solicitarlas nuevamente.'
    },
    {
      question: '¿Puedo realizar prácticas presenciales?',
      answer: 'Sí, si solicitas la opción de prácticas presenciales, estas se realizarán en Pamplona o alrededores. También tienes la opción de crear tu propio vídeo para las prácticas de las unidades 1741_2 y 1742_2. Las unidades 1743_2 y 1744_2 tendrán un coste añadido.'
    },
    {
      question: '¿Qué salidas profesionales tiene?',
      answer: 'Podrás trabajar como adiestrador profesional, educador canino, técnico en modificación de conductas, abrir tu propio centro de adiestramiento o trabajar en residencias, protectoras y centros especializados.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "name": "Contacto | AULA K9",
        "description": "Contacta con AULA K9 para resolver tus dudas sobre nuestros cursos de adiestramiento canino. Estamos aquí para ayudarte.",
        "mainEntity": {
          "@type": "Organization",
          "name": "AULA K9 - Don Bandi El Taller de Pensar",
          "email": "info@aulak9.org",
          "telephone": "+34-722-131-257",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Navarra",
            "addressCountry": "ES"
          }
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contacto | AULA K9 - Don Bandi El Taller de Pensar | Resuelve tus Dudas"
        description="¿Tienes dudas sobre nuestros cursos de adiestramiento canino? Contacta con AULA K9 - Don Bandi El Taller de Pensar. Te ayudamos con información sobre inscripción, certificación oficial, metodología, precios y prácticas presenciales. Horario de tutorías: Sábados 10:00-12:00."
        keywords="contacto AULA K9, información cursos, inscripción adiestramiento canino, dudas certificación, tutorías online, preguntas frecuentes, soporte académico"
        canonical="/contacto"
        schema={schema}
      />
      <Header />
      <main>
        <section className="bg-gradient-to-br from-cyan-400 to-cyan-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold mb-6">
                Contacta con Nosotros
              </h1>
              <p className="text-xl text-cyan-50 leading-relaxed">
                ¿Tienes dudas sobre nuestros cursos? ¿Necesitas más información? Estamos aquí para ayudarte. Te ayudamos con tu perro de forma respetuosa, correcta y profesional.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Envíanos un Mensaje
                </h2>
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-gray-900 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="asunto" className="block text-sm font-semibold text-gray-900 mb-2">
                      Asunto
                    </label>
                    <select
                      id="asunto"
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="informacion-cursos">Información sobre cursos</option>
                      <option value="inscripcion">Proceso de inscripción</option>
                      <option value="certificacion">Certificación oficial</option>
                      <option value="metodologia">Metodología y contenidos</option>
                      <option value="precios">Precios y formas de pago</option>
                      <option value="practicas">Prácticas presenciales</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-900 mb-2">
                      Mensaje * (máximo 500 caracteres)
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Escribe tu mensaje aquí..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.mensaje.length}/500 caracteres
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                      <div>
                        <p className="text-green-800 font-semibold">¡Mensaje enviado con éxito!</p>
                        <p className="text-green-700 text-sm">Te responderemos lo antes posible.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                      <div>
                        <p className="text-red-800 font-semibold">Error al enviar el mensaje</p>
                        <p className="text-red-700 text-sm">Por favor, inténtalo de nuevo.</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-cyan-500 px-6 py-4 text-base font-semibold text-white shadow-lg hover:bg-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                  </button>
                </form>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Información de Contacto
                </h2>
                <div className="space-y-6 mb-12">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-teal-600 text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                      <a 
                        href="mailto:info@aulak9.org" 
                        className="text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                      >
                        info@aulak9.org
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-fill text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Ubicación</h3>
                      <p className="text-gray-600">Comunidad Foral de Navarra, España</p>
                      <p className="text-gray-500 text-sm mt-1">Prácticas presenciales en Pamplona o alrededores</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-fill text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Horario de Tutorías</h3>
                      <p className="text-gray-600">Sábados: 10:00 - 12:00</p>
                      <p className="text-gray-500 text-sm mt-1">Envío de dudas: Domingo a Jueves</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Preguntas Frecuentes
                </h2>
                <div className="space-y-4">
                  {faqs.slice(0, 3).map((faq, index) => (
                    <details
                      key={index}
                      className="bg-gray-50 rounded-xl p-6 cursor-pointer group"
                    >
                      <summary className="font-bold text-gray-900 flex items-center justify-between cursor-pointer">
                        {faq.question}
                        <i className="ri-arrow-down-s-line text-xl text-gray-600 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <p className="text-gray-600 mt-4 leading-relaxed">
                        {faq.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Todas las Preguntas Frecuentes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                >
                  <summary className="font-bold text-gray-900 flex items-center justify-between cursor-pointer">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-xl text-gray-600 group-open:rotate-180 transition-transform flex-shrink-0 ml-2"></i>
                  </summary>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
